import { useRef, useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 白丝文化指南',
    description:
      '白丝文化指南是一个专注于白丝文化与潮流穿搭的综合性内容平台，致力于为白丝爱好者提供最全面、最专业的参考资讯。',
    url: 'https://baisi-style.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '白丝文化指南',
      url: 'https://baisi-style.com',
    },
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }
    try {
      const res = await fetch(
        'https://readdy.ai/api/form/d9q3at4nlsngrm4llqqg',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(
            Array.from(formData.entries()).filter(([k]) => k !== 'phone_alt').map(([k, v]) => [k, v.toString()])
          ).toString(),
        }
      );
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse errors */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        setFormStatus('error');
        const msg = (parsed?.meta as Record<string, string>)?.message || responseText || '提交失败，请稍后再试';
        setFormMessage(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试');
    }
  };

  const teamMembers = [
    {
      name: '丝韵',
      role: '创始人 & 主编',
      bio: '资深时尚编辑，专注白丝文化研究超过8年。致力于通过优质内容传播白丝美学。',
      imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20photography%20of%20young%20Asian%20woman%2C%20clean%20cream%20background%2C%20minimalist%20style%2C%20warm%20natural%20lighting%2C%20fashion%20editorial&width=200&height=200&seq=team-member-1&orientation=squarish',
    },
    {
      name: '小丝',
      role: '资深内容编辑',
      bio: '白丝穿搭达人，拥有丰富的搭配经验。擅长将专业穿搭知识转化为易懂实用的内容。',
      imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20portrait%20of%20young%20Asian%20woman%20with%20warm%20smile%2C%20cream%20studio%20background%2C%20minimalist%20photography%2C%20soft%20natural%20light%2C%20professional%20headshot&width=200&height=200&seq=team-member-2&orientation=squarish',
    },
    {
      name: '白小姐',
      role: '品牌运营总监',
      bio: '负责品牌建设与社区运营，致力于打造最有温度的白丝爱好者社群。',
      imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20portrait%20of%20young%20Asian%20woman%20with%20confident%20expression%2C%20clean%20cream%20background%2C%20minimalist%20aesthetic%2C%20soft%20studio%20lighting%2C%20professional%20look&width=200&height=200&seq=team-member-3&orientation=squarish',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '白丝文化指南是一个什么样的平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '白丝文化指南是一个专注于白丝文化与潮流穿搭的综合性内容平台，为白丝爱好者提供从文化历史、穿搭技巧到选购指南的全方位资讯服务。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向网站投稿或提供建议？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系表单向我们提交合作意向或建议。我们的编辑团队会认真阅读每一条留言，并在适当的时候与您取得联系。',
        },
      },
      {
        '@type': 'Question',
        name: '网站的内容多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们保持每周至少更新3-4篇优质原创内容，涵盖白丝文化、潮流穿搭、选购指南和白丝美学四大板块。关注我们的社交媒体账号可以第一时间获取更新通知。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们｜白丝文化指南 - 品牌介绍、口碑与联系方式"
        description="白丝文化指南是一个专注于白丝文化与潮流穿搭的综合性内容平台，致力于为白丝爱好者提供最全面、最专业的参考资讯。了解我们的品牌故事、编辑团队与联系方式。"
        keywords="白丝文化指南,关于我们,白丝品牌,联系方式,白丝社区"
        canonicalUrl="https://baisi-style.com/about"
        ogType="website"
        schema={schema}
      />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <Navbar />

      <main className="pt-16">
        <section className="w-full bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <Breadcrumb
              items={[{ label: '首页', href: '/' }, { label: '关于我们' }]}
            />
            <div className="mt-6">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
                关于我们
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-2xl leading-relaxed">
                专注白丝文化与潮流穿搭，用心打造最有温度的白丝爱好者社区
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  我们的故事
                </h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>
                    白丝文化指南创立于2024年，源于创始团队对白丝文化与时尚穿搭的深厚热爱。我们发现，虽然白丝爱好者的群体日益壮大，但市面上始终缺少一个专业、全面、有温度的内容平台来满足他们的需求。
                  </p>
                  <p>
                    于是，我们决定自己动手。从最初几个人的小团队开始，我们一步步建立起涵盖白丝文化研究、潮流穿搭指导、选购评测、美学探讨四大板块的内容体系。每一篇文章都经过编辑团队的精心打磨，确保为读者提供有价值的信息和灵感。
                  </p>
                  <p>
                    如今，白丝文化指南已经成为白丝爱好者群体中最受信赖的内容平台之一。但我们知道，这只是一个开始。未来，我们将继续深耕内容品质，拓展更多服务功能，让每一位白丝爱好者都能在这里找到归属感。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20editorial%20workspace%20with%20white%20stockings%20displayed%20artistically%2C%20minimalist%20cream%20interior%2C%20soft%20natural%20lighting%2C%20creative%20studio%20atmosphere%2C%20clean%20aesthetic&width=700&height=500&seq=about-baisi-story&orientation=landscape"
                  alt="白丝文化指南工作室"
                  title="白丝文化指南 - 我们的故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 text-center">
              我们的理念
            </h2>
            <p className="text-foreground-600 text-center mb-12 max-w-2xl mx-auto">
              我们坚信，每一个关于美的追求都值得被认真对待
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-heart-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 whitespace-nowrap">
                  热爱驱动
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  每一篇文章都源于对白丝文化的真挚热爱，而非商业利益的驱动。
                </p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                  <i className="ri-shield-check-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 whitespace-nowrap">
                  专业可靠
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  坚持严谨的内容审核流程，确保每一条信息都经过充分验证。
                </p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-group-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 whitespace-nowrap">
                  社区为先
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  重视每一位社区成员的声音，共同打造温暖有爱的白丝爱好者家园。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 text-center">
              编辑团队
            </h2>
            <p className="text-foreground-600 text-center mb-12 max-w-2xl mx-auto">
              一群热爱白丝文化的内容创作者
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center"
                >
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden">
                    <img
                      src={member.imageUrl}
                      alt={member.name}
                      title={`${member.name} - ${member.role}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1 whitespace-nowrap">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary-600 font-medium mb-3 whitespace-nowrap">
                    {member.role}
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 text-center">
              用户口碑
            </h2>
            <p className="text-foreground-600 text-center mb-12 max-w-2xl mx-auto">
              来自社区的真实声音
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                <div className="flex items-center space-x-1 mb-4 text-accent-500">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;终于找到了一个真正懂白丝文化的平台！文章内容专业又易懂，穿搭建议非常实用，已经推荐给了身边的朋友。&rdquo;
                </p>
                <div>
                  <p className="text-sm font-medium text-foreground-900">小雨</p>
                  <p className="text-xs text-foreground-500">白丝爱好者</p>
                </div>
              </div>
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                <div className="flex items-center space-x-1 mb-4 text-accent-500">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;选购指南那几篇文章帮我省了不少钱！以前总是买不到合适的白丝，现在根据指南挑选，每一次都很满意。&rdquo;
                </p>
                <div>
                  <p className="text-sm font-medium text-foreground-900">阿琳</p>
                  <p className="text-xs text-foreground-500">时尚博主</p>
                </div>
              </div>
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                <div className="flex items-center space-x-1 mb-4 text-accent-500">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;作为一个刚接触白丝的新手，这个网站真的帮了大忙。从文化背景到穿搭技巧，信息非常全面。&rdquo;
                </p>
                <div>
                  <p className="text-sm font-medium text-foreground-900">小月</p>
                  <p className="text-xs text-foreground-500">学生</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 text-center">
              常见问题
            </h2>
            <div className="max-w-2xl mx-auto space-y-3 mt-10">
              <details className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-900">
                    白丝文化指南是一个什么样的平台？
                  </span>
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                  白丝文化指南是一个专注于白丝文化与潮流穿搭的综合性内容平台，为白丝爱好者提供从文化历史、穿搭技巧到选购指南的全方位资讯服务。
                </div>
              </details>
              <details className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-900">
                    如何向网站投稿或提供建议？
                  </span>
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                  您可以通过下方的联系表单向我们提交合作意向或建议。我们的编辑团队会认真阅读每一条留言，并在适当的时候与您取得联系。
                </div>
              </details>
              <details className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-900">
                    网站的内容多久更新一次？
                  </span>
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                  我们保持每周至少更新3-4篇优质原创内容，涵盖白丝文化、潮流穿搭、选购指南和白丝美学四大板块。关注我们的社交媒体账号可以第一时间获取更新通知。
                </div>
              </details>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-center mb-10">
              有任何问题或建议？欢迎通过以下表单与我们联系
            </p>

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-5" data-readdy-form="contact">
              <div>
                <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名
                </label>
                <input
                  id="contact_name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  id="contact_email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900 transition-colors"
                  placeholder="请输入您的邮箱地址"
                />
              </div>
              <div>
                <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact_subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>
              <div>
                <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact_message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900 transition-colors resize-none"
                  placeholder="请输入您的留言内容（不超过500字）"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1">限500字以内</p>
              </div>

              <div className="sr-only">
                <label htmlFor="phone_alt_contact">Phone</label>
                <input
                  id="phone_alt_contact"
                  name="phone_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                提交留言
              </button>

              {formStatus !== 'idle' && (
                <div
                  className={`p-4 rounded-lg text-sm ${
                    formStatus === 'success'
                      ? 'bg-accent-50 text-accent-800 border border-accent-200'
                      : 'bg-primary-50 text-primary-800 border border-primary-200'
                  }`}
                >
                  {formMessage}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}